/**
 * File Name          : datasnap-device.service.ts
 * Author             : Jared Laine Dyreson
 * Version            : 0.1.0
 * Date               : 2026-03-18
 * Description        : Short Description (manual edit required)
 * 
 * Copyright (C) Innovative Design Labs, Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * This file is subject to the terms and conditions defined in
 * file 'IDL_PROJECTTECH_LICENSE.txt', which is part of this source code package.
 */

import { Injectable } from '@angular/core';
import { DatasnapDevice, DatasnapDeviceStatus } from '../models/datasnap-device.model';
import { IdlPhysensService } from '../../../projects/idl-physens/src/public-api';
import { Capacitor } from '@capacitor/core';
import { BehaviorSubject } from 'rxjs';
import { DatasnapAnalysisService } from './datasnap-analysis.service';
import { BLE_CHARGE_ICON, isChargingState } from '../../../projects/idl-physens/src/lib/models/battery_state_char.model';
import { Device_State } from '../../../projects/idl-physens/src/lib/models/physens-devices/physens-device.model';
import { PhysensBleDevice } from '../../../projects/idl-physens/src/lib/models/physens-devices/physens-ble-device.model';
import { BleClient } from '@capacitor-community/bluetooth-le';
import { confirm, alert } from 'devextreme/ui/dialog';
import { RWUpdateServerService } from './rwupdate-server.service';
import { ConsoleCaptureService } from './console-capture.service';


// Outcome of a reselectDevice() call. The verify path uses this so the caller can keep the
// manual "Reconnect to Verify" button active for a retry (cancelled/blocked) instead of treating
// a closed/empty chooser as a failure. 'skipped' is the non-verify path (value unused there).
// 'timeout' is verify-path only: the native requestDevice() call didn't settle within the
// watchdog window (e.g. ChromeOS where it can hang without ever painting a chooser) — the
// in-flight attempt is left running in the background, but the caller is freed to retry.
export type ReselectOutcome = 'selected' | 'cancelled' | 'blocked' | 'skipped' | 'timeout';

@Injectable({
  providedIn: 'root'
})
export class DatasnapDeviceService {

  devices: DatasnapDevice[] = [];
  pendingAlert: Promise<void> | null = null;
  private addDeviceInProgress = false;
  private reselectInProgress = false;
  private reselectTimeoutHandle: ReturnType<typeof setTimeout> | null = null;
  private static readonly RESELECT_TIMEOUT_MS = 30000;
  // Shorter, different-shaped watchdog for the verify path: unlike the non-verify timeout above,
  // firing this does NOT treat the attempt as failed/cancelled — it only frees up reselectInProgress/
  // requestDevicePromise so a stuck requestDevice() (hangs without ever painting a chooser on
  // ChromeOS) doesn't permanently swallow every future tap. The original attempt keeps running in
  // the background in case it was just a slow-but-real chooser.
  private static readonly VERIFY_RESELECT_WATCHDOG_MS = 15000;
  // Same shape as VERIFY_RESELECT_WATCHDOG_MS, but for the initial "Pair Motion Sensor" flow:
  // addDeviceInProgress is only ever cleared in addNewDevice()'s own finally block, which never
  // runs while the awaited scanForDevice() chain is itself hung (e.g. a native requestDevice()
  // that never settles, observed on ChromeOS) — without this, every subsequent tap is silently
  // swallowed by the addDeviceInProgress guard above until a full page refresh.
  private static readonly ADD_DEVICE_WATCHDOG_MS = 15000;

  // RxJS subject/observable for the devices list
  private devicesSubject = new BehaviorSubject<DatasnapDevice[]>(this.devices);
  devices$ = this.devicesSubject.asObservable();

  // helper to emit current devices (call after mutating this.devices)
  emitDevices(): void {
    this.devicesSubject.next([...this.devices]);
  }

  constructor(private idlService: IdlPhysensService, private analysisService: DatasnapAnalysisService, private rwUpdateServerService: RWUpdateServerService, private ccService: ConsoleCaptureService) { }


  private registerNewDevice(physensDevice: PhysensBleDevice, unitId: string): DatasnapDevice {
    physensDevice.connect(4);

    const newDevice = new DatasnapDevice(physensDevice, this.analysisService, unitId, this.rwUpdateServerService, this.ccService);

    physensDevice.battery_state_subject.subscribe((batteryState: BLE_CHARGE_ICON) => {
      if(isChargingState(batteryState)) {
        this.onChargerDetected(newDevice);
      }
    });

    newDevice.recordingCallTimeoutSubject.subscribe((isTimeout) => {
      this.disconnectDevice(newDevice, "Sensor Inactive", "Your sensor has been disconnected due to inactivity.");
    });

    // Cancel auto-reconnect on other devices to prevent them from reconnecting
    // when the user has moved on to this new device
    this.cancelAutoReconnectOnOtherDevices(newDevice);

    this.devices.push(newDevice);
    this.emitDevices();

    return newDevice;
  }

  async addNewDevice(unitId: string): Promise<DatasnapDevice|null> {
    if (this.addDeviceInProgress) {
      console.warn('addNewDevice ignored because a device chooser flow is already in progress.');
      return null;
    }

    if(Capacitor.getPlatform() !== 'web') {
      console.error("DatasnapDeviceService: addNewDevice not implemented for platform " + Capacitor.getPlatform());
      return null;
    }

    this.addDeviceInProgress = true;

    // Guard against a hung native requestDevice() call (chooser never paints, e.g. ChromeOS)
    // permanently wedging addDeviceInProgress — that would silently swallow every future tap
    // on "Pair Motion Sensor" until a full page refresh. On fire, free up our own bookkeeping
    // (and idlService's requestDevicePromise guard) so the next tap starts a fresh chooser
    // request. The original attempt is NOT cancelled — it keeps running in the background and,
    // if it was just a slow-but-real chooser, still registers the device normally when it
    // resolves (same shape as reselectDevice()'s verify-path watchdog).
    let watchdogResolve: ((value: 'timeout') => void) | null = null;
    const watchdogPromise = new Promise<'timeout'>((resolve) => {
      watchdogResolve = resolve;
    });
    const watchdogHandle = setTimeout(() => {
      console.warn(`addNewDevice: requestDevice() still pending after ${DatasnapDeviceService.ADD_DEVICE_WATCHDOG_MS}ms (chooser likely never painted) — freeing up addDeviceInProgress so the next tap can retry; original attempt keeps running in the background`);
      this.addDeviceInProgress = false;
      this.idlService.cancelPendingDeviceRequest();
      watchdogResolve!('timeout');
    }, DatasnapDeviceService.ADD_DEVICE_WATCHDOG_MS);

    const attemptPromise = this.idlService.scanForDevice("MotionDataSnap").then((physensDevice) => {
      clearTimeout(watchdogHandle);
      return this.registerNewDevice(physensDevice, unitId);
    });

    const outcome = await Promise.race([
      attemptPromise.then((newDevice) => ({ newDevice })),
      watchdogPromise.then(() => ({ newDevice: null as DatasnapDevice | null })),
    ]).catch(() => ({ newDevice: null as DatasnapDevice | null }));

    if (outcome.newDevice == null) {
      // Either the watchdog fired (addDeviceInProgress already cleared above) or the chooser
      // was cancelled/blocked — clear it here too in the latter case. attemptPromise, if still
      // pending, keeps running and will self-register via registerNewDevice() above if it
      // eventually resolves.
      this.addDeviceInProgress = false;
      attemptPromise.catch((error) => {
        console.warn('addNewDevice: background chooser attempt ultimately failed:', error);
      });
      return null;
    }

    this.addDeviceInProgress = false;
    return outcome.newDevice;
  }

  private clearReselectInProgress(): void {
    this.reselectInProgress = false;
    if (this.reselectTimeoutHandle !== null) {
      clearTimeout(this.reselectTimeoutHandle);
      this.reselectTimeoutHandle = null;
    }
  }

  async reselectDevice(device: DatasnapDevice, verifyPath = false): Promise<ReselectOutcome> {
    if (this.reselectInProgress) {
      // Chrome rejects a second navigator.bluetooth.requestDevice() call with
      // SecurityError while the first chooser dialog is still open on screen, even
      // though the click that triggered it was a genuine user gesture. Opening a
      // second chooser here (as this used to do, by cancelling the "stale" attempt
      // and falling through) is exactly what produced that error — bail out instead
      // and let the in-flight attempt (or its own 30s timeout) finish on its own.
      console.warn('reselectDevice already in progress; ignoring duplicate request until the current chooser settles.');
      return Promise.resolve('skipped');
    }

    this.reselectInProgress = true;
    // Guard against the chooser promise never settling (e.g. a BLE disconnect mid-update can
    // leave it hung, or on ChromeOS the native requestDevice() promise itself can simply never
    // resolve/reject). clearReselectInProgress() below only resets our own bookkeeping — it does
    // NOT make the hung scanForDevice() chain below settle, so the caller's awaited promise (and
    // therefore the "Reconnect Device" button's disabled/"Connecting..." state) would otherwise
    // be stuck forever. Race the real attempt against this timeout so the method always resolves
    // within RESELECT_TIMEOUT_MS regardless of whether the browser's chooser ever answers.
    // Skipped on the verify path: there a chooser sitting open for the user is the EXPECTED
    // state, not a failure, so tearing it down after 30s (cancelPendingDeviceRequest) is wrong.
    let timeoutReject: (() => void) | null = null;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutReject = () => reject(new Error('reselectDevice timed out waiting on the chooser flow'));
    });
    if (!verifyPath) {
      this.reselectTimeoutHandle = setTimeout(() => {
        console.warn('reselectDevice timed out waiting on the chooser flow; clearing in-progress flag so reselect can be retried.');
        this.idlService.cancelPendingDeviceRequest();
        timeoutReject!();
      }, DatasnapDeviceService.RESELECT_TIMEOUT_MS);
    }

    if(Capacitor.getPlatform() !== 'web') {
      console.error("DatasnapDeviceService: reselectDevice not implemented for platform " + Capacitor.getPlatform());
      this.clearReselectInProgress();
      return Promise.resolve('skipped');
    }

    // Diagnostics for the verify path: capture which device the chooser returned (name/id) and
    // how long it stayed open, so a chooser that paints empty / is dismissed is distinguishable
    // from one that resolved with a real selection.
    let selectedDeviceInfo: { name?: string; id?: string } | null = null;
    const verifyT0 = Date.now();

    // Verify path: we already know exactly which unit we're waiting for, so filter the chooser
    // down to that exact device name (not just a prefix) — prevents picking the wrong DataSnap
    // when multiple units are in range. Falls back to the generic prefix if the name wasn't
    // captured (shouldn't normally happen on this path).
    // Non-verify path: use the generic product prefix, not the specific device's own name, so the
    // chooser shows every DataSnap unit in range (including a different device the user may want
    // to switch to) instead of only the one that just went out of range.
    const namePrefix = "MotionDataSnap";
    const exactName = verifyPath && !!device.deviceName;
    const nameFilter = exactName ? device.deviceName! : namePrefix;
    console.debug(`Reselect requested for ${device.deviceId ?? 'unknown'} using ${exactName ? 'exact name' : 'prefix'} ${nameFilter}`);
    if (verifyPath) console.log('[FWUpdate] reselect(verify): chooser open');

    // ChromeOS: a background BleClient.connect() to the out-of-range device can hold BleClient's
    // serial queue for its full 20s timeout, so the queued requestDevice() only reaches the
    // browser after the click's ~5s user activation has expired — the chooser never paints and
    // eventually rejects with SecurityError ("Must be handling a user gesture"). Abandon that
    // attempt and reset the queue first. Must stay synchronous (no await) so we're still inside
    // the user gesture when requestDevice() runs. Skipped if the caller already paused the loop.
    const pausedForChooser = DatasnapDeviceService.isChromeOS() && !device.autoReconnectPaused;
    if (pausedForChooser) {
      console.log('[BLE] ChromeOS: pausing auto-reconnect and resetting BLE queue before opening chooser');
      device.pauseAutoReconnectForVerify();
    }
    // On a successful selection the wrapper has already swapped to the new (unpaused) instance,
    // so this only matters when the chooser is cancelled/blocked/timed out.
    const resumeAfterChooser = () => {
      if (pausedForChooser) device.resumeAutoReconnectForVerify();
    };

    const attemptPromise = this.idlService.scanForDevice(nameFilter, exactName).then(async (newPhysensDevice) => {
      const sameDevice = newPhysensDevice.deviceId === device.deviceId;
      selectedDeviceInfo = { name: newPhysensDevice.deviceName, id: newPhysensDevice.deviceId };
      console.log(sameDevice
        ? "Reselected same device, retrying connection"
        : `Different device selected during reselect. Previous: ${device.deviceId ?? 'unknown'} Selected: ${newPhysensDevice.deviceId}`);

      // Always swap in the instance scanForDevice just created/registered, even when the
      // user reselected the "same" device — idlService.scanForDevice() always constructs
      // a fresh PhysensBleDevice, so reusing the wrapper's previous device reference here
      // would keep driving a stale instance that idlService's registry (and therefore
      // manual-disconnect cleanup) no longer tracks, letting it auto-reconnect unchecked.
      device.reselectDevice(newPhysensDevice);

      // Set up battery charging subscription for new device
      newPhysensDevice.battery_state_subject.subscribe((batteryState) => {
        if(isChargingState(batteryState)) {
          this.onChargerDetected(device);
        }
      });

      newPhysensDevice.resetConnectionAttempts();
      // Clear out any stale/half-open GATT session before reconnecting — matches what
      // the automatic background reconnect loop does; skipping it left manual reselect
      // retries silently timing out.
      await newPhysensDevice.cleanupStaleConnection();
      newPhysensDevice.connect(4);

      // Cancel auto-reconnect on other devices when switching to this device
      this.cancelAutoReconnectOnOtherDevices(device);
    });

    if (verifyPath) {
      // No "failure" timeout race on the verify path — the chooser is allowed to stay open as
      // long as the user needs. But a stuck native requestDevice() call (ChromeOS can hang it
      // without ever painting a chooser) must not wedge reselectInProgress/requestDevicePromise
      // forever, or every future tap is silently swallowed until a full page refresh. Race a
      // short watchdog that, on firing, frees up our own bookkeeping (letting the next tap issue
      // a fresh requestDevice() call) WITHOUT touching the original attempt — it keeps running in
      // the background in case it was just a slow-but-real chooser, and its .then() above still
      // applies the selection normally if/when it resolves.
      let watchdogResolve: ((value: 'timeout') => void) | null = null;
      const watchdogPromise = new Promise<'timeout'>((resolve) => {
        watchdogResolve = resolve;
      });
      const watchdogHandle = setTimeout(() => {
        console.warn(`[FWUpdate] reselect(verify): requestDevice() still pending after ${DatasnapDeviceService.VERIFY_RESELECT_WATCHDOG_MS}ms (chooser likely never painted) — freeing up reselect state so the next tap can retry; original attempt keeps running in the background`);
        // Clears ONLY the idlService-level guard so the next scanForDevice() issues a fresh
        // native requestDevice() call instead of reusing this hung promise. Does not touch the
        // hung native call itself (no API to abort it) — if it's actually still a live chooser,
        // a second concurrent requestDevice() throws SecurityError, which is already mapped to
        // 'blocked' above with a clear retry message.
        this.idlService.cancelPendingDeviceRequest();
        watchdogResolve!('timeout');
      }, DatasnapDeviceService.VERIFY_RESELECT_WATCHDOG_MS);

      return Promise.race([
        attemptPromise.then(() => {
          console.log(`[FWUpdate] reselect(verify): chooser resolved name=${selectedDeviceInfo?.name ?? '?'} id=${selectedDeviceInfo?.id ?? '?'} in ${Date.now() - verifyT0}ms`);
          clearTimeout(watchdogHandle);
          return 'selected' as const;
        }).catch((error): ReselectOutcome => {
          const name = (error as { name?: string })?.name ?? 'UnknownError';
          console.warn(`[FWUpdate] reselect(verify): chooser rejected(${name}) in ${Date.now() - verifyT0}ms`);
          clearTimeout(watchdogHandle);
          if (name === 'SecurityError' || name === 'NotAllowedError') {
            console.error(`Reselect device failed. Browser may have blocked chooser or chooser was dismissed. ${String(error)}`);
            return 'blocked';
          }
          return 'cancelled';
        }),
        watchdogPromise,
      ]).finally(() => { this.clearReselectInProgress(); resumeAfterChooser(); });
    }

    return Promise.race([attemptPromise, timeoutPromise]).then(() => 'skipped' as const).catch((error) => {
      console.error(`Reselect device failed. Browser may have blocked chooser or chooser was dismissed. ${String(error)}`);
      return 'skipped' as const;
    }).finally(() => {
      this.clearReselectInProgress();
      resumeAfterChooser();
    });
  }

  private static isChromeOS(): boolean {
    return typeof navigator !== 'undefined' && /\bCrOS\b/.test(navigator.userAgent);
  }

  private cancelAutoReconnectOnOtherDevices(currentDevice: DatasnapDevice): void {
    this.devices.forEach((device) => {
      if (device !== currentDevice && device.hasDevice) {
        const physensDevice = this.idlService.getDevice(device.deviceId!);
        if (physensDevice && physensDevice.deviceState !== Device_State.READY_FOR_PACKETS) {
          physensDevice.cancelAutoReconnect();
        }
      }
    });
  }

  async rebootDevice(device: DatasnapDevice) {
    await device.rebootDevice();
    // Drop the device so the auto-reconnect loop doesn't fight the reboot; the user reconnects it.
    this.removeDevice(device);
  }

  removeDevice(device: DatasnapDevice) {
    device.deviceColor = null;
    device.deviceColorRGB = null;
    device.clearTimers();
    device.onRemove();
    // Mark the device's own PhysensBleDevice instance as user-disconnected immediately —
    // this previously only happened indirectly via idlService.removeDevice() looking up
    // the registry by deviceId, which silently no-ops if a reselect ever left a stale
    // instance in that registry slot instead of this device's actual instance.
    device.callRemoveDevice();

    const idx = this.devices.findIndex((sDevice)=>(sDevice.deviceId == device.deviceId))
    if(idx < 0) return;

    if(device.hasDevice) {
      this.idlService.removeDevice(device.deviceId!)
    }

    this.devices.splice(idx, 1);
    this.emitDevices();
  }

  removeAllDevices() {
    for(let device of this.devices) {
      device.deviceColor = null;
      device.deviceColorRGB = null;
      device.clearTimers();
      if(device.hasDevice) {
        this.idlService.removeDevice(device.deviceId!)
      }
      
    }
    this.devices = [];
    this.emitDevices();
  }

  

  // Regulatory requirement (bug 20264): a DataSnap must never communicate while charging, and
  // that includes a firmware update. v1.0.0 did this unconditionally; it was later suppressed
  // during OTA by the currentlyUpdating guard in disconnectDevice() (9996905). The charger path
  // now aborts the update first (no further mcumgr writes) and then always disconnects.
  onChargerDetected(device: DatasnapDevice) {
    if(!device.currentlyUpdating) {
      this.disconnectDevice(device, "Device Charging", "Your sensor has been placed on the charger and has been disconnected.");
      return;
    }
    console.warn('[FWUpdate] charger detected during firmware update - aborting update and disconnecting');
    device.abortUpdate('Firmware update stopped: device placed on charger');
    this.disconnectDevice(device, "Update Stopped",
      "Your sensor was placed on the charger, so the firmware update was stopped and the sensor was disconnected. Remove it from the charger, reconnect it, and try the update again.",
      true);
  }

  // force: used only by the charger path above. Bypasses the "an alert is already showing"
  // and mid-OTA guards, which may suppress the alert but must never suppress the disconnect.
  disconnectDevice(device: DatasnapDevice, title: string, message: string, force = false) {
    if(!device.hasDevice) return;
    if(this.pendingAlert && !force) return;
    // A firmware update deliberately reboots the device and expects the resulting
    // disconnect/reconnect cycle — tearing the device down here for a stray/garbled
    // inactivity signal mid-update would strand the update UI (dropping its device
    // reference) and pop a disconnect alert over an in-progress OTA.
    if(device.currentlyUpdating && !force) return;

    if(!this.pendingAlert) {
      this.pendingAlert = alert(message, title);
      this.pendingAlert.then(() => {
        this.pendingAlert = null;
      }).catch((error) => {
        console.error("Error showing alert dialog:", error);
        this.pendingAlert = null;
      });
    }
    if(device.status !== DatasnapDeviceStatus.DISPLAYING) {
      this.removeDevice(device);
      return;
    }
    device.callRemoveDevice();
    device.deviceColor = null;
    device.deviceColorRGB = null;
    device.onRemove();
    device.clearTimers();
    BleClient.disconnect(IdlPhysensService.hexToString(device.deviceId!)).then(()=>{
      device.setDeviceNull();
      this.emitDevices();
    })
  }

  clearDisconnectedDevices() {
    this.devices = this.devices.filter((device)=>(device.hasDevice));
    this.emitDevices();
  }
}
